import { OrganizationService } from '@/services/organizations';
import { useOrganizationStore } from '@/stores/organizationStore';
import { getCurrentOrganization } from '@/lib/organizationBridge';
import { queryClient } from '@/lib/queryClient';
import { queryKeys } from '@/lib/queryKeys';
import type {
  BluetoothIosPrintersConfig,
  EpsonPrintersConfig,
  GenericEscposPrintersConfig,
  PrinterRoutingConfig,
  UsbAndroidPrintersConfig,
  UsbWindowsPrintersConfig,
} from './types';

interface OrganizationPrinterSettings {
  /** Legacy ePOS JS SDK setting. Unused; kept so writes do not drop existing org JSON. */
  epsonPrintersConfig?: EpsonPrintersConfig;
  genericEscposPrintersConfig?: GenericEscposPrintersConfig;
  usbAndroidPrintersConfig?: UsbAndroidPrintersConfig;
  usbWindowsPrintersConfig?: UsbWindowsPrintersConfig;
  bluetoothIosPrintersConfig?: BluetoothIosPrintersConfig;
  printerRoutingConfig?: PrinterRoutingConfig;
}

type PrinterSettingsKey = keyof OrganizationPrinterSettings;

const ORGANIZATION_SETTINGS_KEY = 'printerSettings';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getOrganizationPrinterSettings = (): Partial<OrganizationPrinterSettings> => {
  const organization = getCurrentOrganization();
  if (!organization || !isPlainObject(organization.settings)) {
    return {};
  }

  const printerSettings = organization.settings[ORGANIZATION_SETTINGS_KEY];
  return isPlainObject(printerSettings) ? (printerSettings as Partial<OrganizationPrinterSettings>) : {};
};

export const readPrinterSetting = <T>(key: PrinterSettingsKey): T | null => {
  const organizationSettings = getOrganizationPrinterSettings();
  const dbValue = organizationSettings[key] as T | undefined;
  return dbValue ?? null;
};

export const writePrinterSetting = async <T>(key: PrinterSettingsKey, value: T): Promise<void> => {
  const organization = getCurrentOrganization();
  const organizationSlug = useOrganizationStore.getState().currentOrganizationSlug;

  if (!organization || !organizationSlug) {
    throw new Error('No current organization selected');
  }

  const currentOrganizationSettings = isPlainObject(organization.settings)
    ? organization.settings
    : {};
  const currentPrinterSettings = getOrganizationPrinterSettings();
  const nextPrinterSettings: OrganizationPrinterSettings = {
    ...currentPrinterSettings,
    [key]: value,
  };

  const updatedOrganization = await OrganizationService.updateOrganization(organization.id, {
    settings: {
      ...currentOrganizationSettings,
      [ORGANIZATION_SETTINGS_KEY]: nextPrinterSettings,
    },
  });

  queryClient.setQueryData(queryKeys.organization(organizationSlug), (previous: unknown) => {
    const prevObject =
      previous && typeof previous === 'object'
        ? (previous as Record<string, unknown>)
        : {};

    return {
      ...prevObject,
      organization: updatedOrganization,
    };
  });
};
